<template>
  <v-card height="100%" :loading="loading" flat color="bg1" class="sso-settings overflow-y-auto">
    <!-- Page Header -->
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title d-flex align-center">
          Single Sign-On (SSO)
          <v-tooltip bottom max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-2" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <span>Enable secure authentication through identity providers. Users can sign in with their existing corporate credentials.</span>
          </v-tooltip>
        </v-list-item-title>
        <v-list-item-subtitle>Configure identity provider connections for your organization</v-list-item-subtitle>
      </v-list-item-content>
    </v-list-item>
    <v-divider></v-divider>

    <!-- Provider Connection Section -->
    <v-sheet class="ma-5 ml-8">
      <v-expansion-panels v-model="expandedPanels" multiple flat>
        <!-- Identity Provider Connection -->
        <v-expansion-panel class="sso-panel">
          <v-expansion-panel-header class="sso-panel-header">
            <div class="d-flex align-center">
              <div class="sso-panel-icon mr-3" :class="{ 'sso-panel-icon--connected': isConnected }">
                <v-icon :color="isConnected ? 'white' : 'grey'" small>mdi-shield-account</v-icon>
              </div>
              <div>
                <span class="sso-panel-title">Identity Provider</span>
                <v-chip
                  x-small
                  :color="isConnected ? 'success' : undefined"
                  :text-color="isConnected ? 'white' : undefined"
                  :outlined="!isConnected"
                  class="ml-2">
                  {{ isConnected ? 'Connected' : 'Not Connected' }}
                </v-chip>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <!-- Not Connected State -->
            <div v-if="!isConnected" class="sso-connect-section">
              <div class="sso-microsoft-card">
                <div class="sso-microsoft-card__header">
                  <MicrosoftLogo class="sso-provider-logo" />
                  <div class="sso-microsoft-card__info">
                    <span class="sso-microsoft-card__name">Microsoft Entra ID</span>
                    <span class="sso-microsoft-card__desc">Azure Active Directory</span>
                  </div>
                </div>
                <v-btn
                  :loading="connectLoading"
                  color="primary"
                  class="sso-connect-btn"
                  @click="initiateAdminConsent">
                  <v-icon left small>mdi-link</v-icon>
                  Connect
                </v-btn>
              </div>
              <p class="sso-admin-hint">
                <v-icon x-small color="grey">mdi-shield-account</v-icon>
                Requires Azure AD Global Administrator or Application Administrator privileges
              </p>
            </div>

            <!-- Connected State -->
            <div v-else class="sso-connected-section">
              <div class="sso-connected-row">
                <div class="sso-connected-provider">
                  <MicrosoftLogo class="sso-provider-logo--small" />
                  <div class="sso-connected-info">
                    <span class="sso-connected-name">{{ providerName }}</span>
                    <!-- <span class="sso-connected-tenant">Tenant: {{ ssoSettings.azureTenantId }}</span> -->
                    <span v-if="ssoSettings.consentGrantedByName" class="sso-connected-meta">
                      Connected by {{ ssoSettings.consentGrantedByName }}
                    </span>
                  </div>
                </div>
                <div class="sso-connected-actions">
                  <v-btn icon small :loading="testConnection.loading" @click="testSSOConnection" title="Test Connection">
                    <v-icon small :color="testConnection.success ? 'success' : testConnection.error ? 'error' : 'grey'">
                      {{ testConnection.success ? 'mdi-check-circle' : testConnection.error ? 'mdi-alert-circle' : 'mdi-connection' }}
                    </v-icon>
                  </v-btn>
                  <v-btn icon small color="error" @click="confirmDisconnect" title="Disconnect">
                    <v-icon small>mdi-link-off</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-alert v-if="testConnection.message" dense text :color="testConnection.success ? 'success' : 'error'" class="mt-2 mb-0">
                {{ testConnection.message }}
              </v-alert>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Authentication Settings (Only when connected) -->
        <v-expansion-panel v-if="isConnected" class="sso-panel">
          <v-expansion-panel-header class="sso-panel-header">
            <div class="d-flex align-center">
              <div class="sso-panel-icon mr-3">
                <v-icon color="grey" small>mdi-cog</v-icon>
              </div>
              <span class="sso-panel-title">Authentication Settings</span>
              <v-chip v-if="hasAuthChanges" x-small color="warning" text-color="white" class="ml-2">Unsaved</v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="sso-settings-list">
              <!-- Enable SSO -->
              <div class="sso-setting-row">
                <div class="sso-setting-info">
                  <span class="sso-setting-label">
                    Enable SSO Login
                    <v-tooltip bottom max-width="250">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                      </template>
                      <span>When enabled, "Sign in with Microsoft" button appears on the login page.</span>
                    </v-tooltip>
                  </span>
                </div>
                <v-switch v-model="authSettings.enableSsoLogin" hide-details dense color="primary" class="mt-0 pt-0"></v-switch>
              </div>

              <!-- Allow Password Login -->
              <div class="sso-setting-row">
                <div class="sso-setting-info">
                  <span class="sso-setting-label">
                    Allow Password Login
                    <v-tooltip bottom max-width="250">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                      </template>
                      <span>Allow users to sign in with email/password alongside SSO. Disable to enforce SSO-only authentication.</span>
                    </v-tooltip>
                  </span>
                  <span v-if="!authSettings.allowPasswordLogin && authSettings.enableSsoLogin" class="sso-setting-warning">
                    <v-icon x-small color="warning">mdi-alert</v-icon>
                    SSO-only mode
                  </span>
                </div>
                <v-switch v-model="authSettings.allowPasswordLogin" hide-details dense color="primary" class="mt-0 pt-0"></v-switch>
              </div>

              <!-- Save/Discard buttons for Authentication Settings -->
              <div v-if="hasAuthChanges" class="sso-section-actions">
                <v-btn small color="primary" :loading="savingAuth" @click="saveAuthSettings">
                  <v-icon left x-small>mdi-content-save</v-icon>
                  Save
                </v-btn>
                <v-btn small text color="grey" @click="resetAuthSettings" class="ml-2">Discard</v-btn>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- User Provisioning (Only when connected) -->
        <v-expansion-panel v-if="isConnected" class="sso-panel">
          <v-expansion-panel-header class="sso-panel-header">
            <div class="d-flex align-center">
              <div class="sso-panel-icon mr-3">
                <v-icon color="grey" small>mdi-account-plus</v-icon>
              </div>
              <span class="sso-panel-title">User Provisioning</span>
              <v-chip v-if="provisioningSettings.autoCreateUsers" x-small color="blue" text-color="white" class="ml-2">Auto</v-chip>
              <v-chip v-if="hasProvisioningChanges" x-small color="warning" text-color="white" class="ml-2">Unsaved</v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="sso-settings-list">
              <!-- Auto Provisioning -->
              <div class="sso-setting-row">
                <div class="sso-setting-info">
                  <span class="sso-setting-label">
                    Auto-create Users
                    <v-tooltip bottom max-width="280">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                      </template>
                      <span>Automatically create accounts when users sign in via SSO for the first time. Profile info (name, email) is synced from the identity provider.</span>
                    </v-tooltip>
                  </span>
                </div>
                <v-switch v-model="provisioningSettings.autoCreateUsers" hide-details dense color="blue" class="mt-0 pt-0"></v-switch>
              </div>

              <!-- Default Role (visible when auto-provision enabled) -->
              <v-expand-transition>
                <div v-if="provisioningSettings.autoCreateUsers" class="sso-role-row">
                  <v-label class="sso-label">
                    Default Role
                    <v-tooltip bottom max-width="250">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                      </template>
                      <span>Role assigned to auto-created users. Can be changed later per user.</span>
                    </v-tooltip>
                  </v-label>
                  <RolesSelector
                    v-model="provisioningSettings.defaultRoleId"
                    :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                    placeholder="Select role"
                    outlined
                    dense
                    hide-details
                    class="sso-role-select" />
                  <span v-if="provisioningSettings.defaultRoleName" class="sso-role-current">
                    Current: {{ provisioningSettings.defaultRoleName }}
                  </span>
                </div>
              </v-expand-transition>

              <!-- Default Facilities (visible when auto-provision enabled) -->
              <v-expand-transition>
                <div v-if="provisioningSettings.autoCreateUsers" class="sso-role-row">
                  <v-label class="sso-label">
                    Default Facilities
                    <v-tooltip bottom max-width="250">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon x-small v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                      </template>
                      <span>Facilities assigned to auto-created users. Users will have access to these facilities upon first SSO login.</span>
                    </v-tooltip>
                  </v-label>
                  <v-autocomplete
                    v-model="provisioningSettings.facilityIds"
                    :items="facilities"
                    :loading="facilitiesLoading"
                    item-text="name"
                    item-value="id"
                    multiple
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    hide-details
                    placeholder="Select facilities"
                    :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                    class="sso-facilities-select">
                    <!-- Chip overflow: show max 2 chips, then "+N more" -->
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        v-if="index < 2"
                        small
                        close
                        class="ma-1"
                        @click:close="removeFacility(item.id)">
                        {{ item.name }}
                      </v-chip>
                      <v-chip
                        v-else-if="index === 2"
                        small
                        color="grey lighten-2"
                        class="ma-1">
                        +{{ provisioningSettings.facilityIds.length - 2 }} more
                      </v-chip>
                    </template>
                    <!-- Select All / Deselect All header -->
                    <template v-slot:prepend-item>
                      <div class="px-4 py-2 d-flex align-center justify-space-between">
                        <div class="d-flex align-center" style="gap: 4px;">
                          <v-btn x-small text color="primary" @click="selectAllFacilities">Select All</v-btn>
                          <v-btn x-small text @click="deselectAllFacilities">Deselect All</v-btn>
                        </div>
                        <v-chip v-if="provisioningSettings.facilityIds.length > 0" x-small color="primary" text-color="white">
                          {{ provisioningSettings.facilityIds.length }} selected
                        </v-chip>
                      </div>
                      <v-divider></v-divider>
                    </template>
                  </v-autocomplete>
                </div>
              </v-expand-transition>

              <!-- Save/Discard buttons for User Provisioning -->
              <div v-if="hasProvisioningChanges" class="sso-section-actions">
                <v-btn small color="primary" :loading="savingProvisioning" @click="saveProvisioningSettings">
                  <v-icon left x-small>mdi-content-save</v-icon>
                  Save
                </v-btn>
                <v-btn small text color="grey" @click="resetProvisioningSettings" class="ml-2">Discard</v-btn>
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Verified Domains (Only when connected) -->
        <v-expansion-panel v-if="isConnected" class="sso-panel">
          <v-expansion-panel-header class="sso-panel-header">
            <div class="d-flex align-center">
              <div class="sso-panel-icon mr-3">
                <v-icon color="grey" small>mdi-domain</v-icon>
              </div>
              <span class="sso-panel-title">Verified Domains</span>
              <v-chip x-small class="ml-2" :color="verifiedDomains.length ? 'primary' : undefined" :outlined="!verifiedDomains.length">
                {{ verifiedDomains.length }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="sso-domains-section">
              <!-- Domains Description -->
              <p class="sso-domains-description">
                <v-icon x-small color="grey" class="mr-1">mdi-information-outline</v-icon>
                Users with email addresses matching these domains can sign in using SSO.
              </p>

              <!-- Domain List -->
              <div v-if="verifiedDomains.length" class="sso-domains-list">
                <div
                  v-for="domain in verifiedDomains"
                  :key="domain.id"
                  class="sso-domain-item">
                  <div class="sso-domain-info">
                    <div class="sso-domain-name">
                      <v-icon x-small color="success" class="mr-1">mdi-check-circle</v-icon>
                      {{ domain.domain }}
                    </div>
                    <div class="sso-domain-meta">
                      <span v-if="domain.notes">{{ domain.notes }}</span>
                      <span v-if="domain.createdByName"> · Added by {{ domain.createdByName }}</span>
                      <span v-if="domain.createDate"> · {{ formatDate(domain.createDate) }}</span>
                    </div>
                  </div>
                  <v-btn
                    icon
                    x-small
                    color="error"
                    :loading="deletingDomainId === domain.id"
                    @click="confirmDeleteDomain(domain)"
                    title="Remove domain">
                    <v-icon x-small>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="sso-domains-empty">
                <v-icon color="grey lighten-1">mdi-domain</v-icon>
                <span>No verified domains configured</span>
              </div>

              <!-- Add Domain Button -->
              <v-btn
                text
                small
                color="primary"
                class="sso-add-domain-btn mt-3"
                @click="openAddDomainDialog">
                <v-icon left small>mdi-plus</v-icon>
                Add Domain
              </v-btn>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-sheet>

    <!-- Disconnect Confirmation Dialog -->
    <v-dialog v-model="disconnectDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon left small color="error">mdi-alert</v-icon>
          Disconnect SSO?
        </v-card-title>
        <v-card-text class="body-2">
          This will remove the SSO connection. Users who only authenticate via SSO will lose access.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="disconnectDialog = false">Cancel</v-btn>
          <v-btn color="error" small :loading="disconnecting" @click="disconnectSSO">Disconnect</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Domain Dialog -->
    <v-dialog v-model="addDomainDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon left small color="primary">mdi-domain</v-icon>
          Add Verified Domain
        </v-card-title>
        <v-card-text>
          <v-form ref="addDomainForm" v-model="addDomainForm.valid" @submit.prevent="addDomain">
            <v-text-field
              v-model="addDomainForm.domain"
              label="Domain"
              placeholder="example.com"
              :rules="[formRules.required, domainFormatRule]"
              outlined
              dense
              autofocus
              hint="Enter the email domain (e.g., company.com)"
              persistent-hint
              class="mb-2">
              <template v-slot:prepend-inner>
                <span class="grey--text text--darken-1">@</span>
              </template>
            </v-text-field>
            <v-textarea
              v-model="addDomainForm.notes"
              label="Notes (optional)"
              placeholder="e.g., Primary company domain"
              outlined
              dense
              rows="2"
              hide-details />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="closeAddDomainDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            small
            :loading="addDomainForm.loading"
            :disabled="!addDomainForm.valid"
            @click="addDomain">
            Add Domain
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Domain Confirmation Dialog -->
    <v-dialog v-model="deleteDomainDialog" max-width="400">
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon left small color="error">mdi-alert</v-icon>
          Remove Domain?
        </v-card-title>
        <v-card-text class="body-2">
          <p class="mb-2">
            Are you sure you want to remove <strong>{{ domainToDelete?.domain }}</strong>?
          </p>
          <p class="mb-0 grey--text text--darken-1">
            Users with this email domain will no longer be able to sign in via SSO.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text small @click="deleteDomainDialog = false">Cancel</v-btn>
          <v-btn color="error" small :loading="deletingDomainId !== null" @click="deleteDomain">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import permissions from '@Share/config/permissions';
import { $has } from '@/utils';
import formRules from '@Share/config/form-rules';
import ssoService from '@/features/Company/service/sso-settings.service';
import { error } from '@/utils/debugLogger';
import RolesSelector from '@Share/components/RolesSelector.vue';

// Provider Logo Components
const MicrosoftLogo = {
  functional: true,
  render(h) {
    return h('svg', { attrs: { viewBox: '0 0 23 23', width: '20', height: '20' } }, [
      h('rect', { attrs: { x: '1', y: '1', width: '10', height: '10', fill: '#f25022' } }),
      h('rect', { attrs: { x: '12', y: '1', width: '10', height: '10', fill: '#7fba00' } }),
      h('rect', { attrs: { x: '1', y: '12', width: '10', height: '10', fill: '#00a4ef' } }),
      h('rect', { attrs: { x: '12', y: '12', width: '10', height: '10', fill: '#ffb900' } }),
    ]);
  },
};

export default {
  name: 'SSOSettings',
  components: {
    RolesSelector,
    MicrosoftLogo,
  },
  data() {
    return {
      loading: false,
      connectLoading: false,
      disconnecting: false,
      disconnectDialog: false,
      expandedPanels: [0],
      permissions,
      formRules,
      ssoSettings: {
        id: null,
        companyId: null,
        enabled: false,
        displayName: 'Login with Microsoft',
        azureTenantId: null,
        azureClientId: null,
        hasClientSecret: false,
        defaultRoleId: null,
        defaultRoleName: null,
        allowPasswordLogin: true,
        autoProvisionUsers: false,
        allowedEmailDomains: null,
        useSharedApp: true,
        organizationName: null,
        consentGrantedAt: null,
        consentGrantedById: null,
        consentGrantedByName: null,
        verifiedDomains: [],
        createDate: null,
        updateDate: null,
      },
      originalSettings: null,
      testConnection: {
        loading: false,
        success: false,
        error: false,
        message: '',
      },
      // Separate settings tracking
      authSettings: {
        enableSsoLogin: false,
        allowPasswordLogin: true,
      },
      originalAuthSettings: null,
      savingAuth: false,

      provisioningSettings: {
        autoCreateUsers: false,
        defaultRoleId: null,
        defaultRoleName: null,
        facilityIds: [],
      },
      originalProvisioningSettings: null,
      savingProvisioning: false,

      // Domain management
      addDomainDialog: false,
      addDomainForm: {
        valid: false,
        loading: false,
        domain: '',
        notes: '',
      },
      deleteDomainDialog: false,
      domainToDelete: null,
      deletingDomainId: null,
    };
  },
  computed: {
    ...mapGetters('ACCOUNT', {
      user: 'GET_USER',
    }),
    isConnected() {
      return !!(this.ssoSettings.consentGrantedAt && this.ssoSettings.azureTenantId);
    },
    hasAuthChanges() {
      return JSON.stringify(this.authSettings) !== JSON.stringify(this.originalAuthSettings);
    },
    hasProvisioningChanges() {
      return JSON.stringify(this.provisioningSettings) !== JSON.stringify(this.originalProvisioningSettings);
    },
    providerName() {
      return   'Microsoft Entra ID';
    },
    verifiedDomains() {
      return this.ssoSettings.verifiedDomains || [];
    },
    facilities() {
      return this.$store.getters['FACILITY/GET_ACCESSIBLE_ENTRIES'];
    },
    facilitiesLoading() {
      return this.$store.getters['FACILITY/GET_ACCESSIBLE_LOADING'];
    },
    domainFormatRule() {
      return (v) => {
        if (!v) return true;
        // Simple domain validation - no protocol, no path, just domain
        const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
        return domainRegex.test(v) || 'Enter a valid domain (e.g., company.com)';
      };
    },
  },
  methods: {
    $has,
    async loadSettings() {
      this.loading = true;
      try {
        // Load main SSO configuration
        const res = await ssoService.getSettings();
        this.ssoSettings = { ...this.ssoSettings, ...res.data };
        this.originalSettings = structuredClone(this.ssoSettings);

        // If connected, load separate settings from dedicated endpoints
        if (this.isConnected) {
          await this.loadAuthSettings();
          await this.loadProvisioningSettings();
          this.expandedPanels = [0, 1, 2, 3];
        }
      } catch (err) {
        error('Failed to load SSO settings:', err);
        this.$dialog.notify.error('Failed to load SSO settings');
      } finally {
        this.loading = false;
      }
    },

    async loadAuthSettings() {
      try {
        const res = await ssoService.getAuthenticationSettings();
        this.authSettings = { ...res.data };
        this.originalAuthSettings = structuredClone(this.authSettings);
      } catch (err) {
        error('Failed to load authentication settings:', err);
        // Fallback to main config data
        this.authSettings = {
          enableSsoLogin: this.ssoSettings.enabled || false,
          allowPasswordLogin: this.ssoSettings.allowPasswordLogin !== false,
        };
        this.originalAuthSettings = structuredClone(this.authSettings);
      }
    },

    async loadProvisioningSettings() {
      try {
        this.$store.dispatch('FACILITY/FETCH_ACCESSIBLE');
        const res = await ssoService.getProvisioningSettings();
        this.provisioningSettings = {
          ...res.data,
          facilityIds: res.data.facilityIds || [],
        };
        this.originalProvisioningSettings = structuredClone(this.provisioningSettings);
      } catch (err) {
        error('Failed to load provisioning settings:', err);
        // Fallback to main config data
        this.provisioningSettings = {
          autoCreateUsers: this.ssoSettings.autoProvisionUsers || false,
          defaultRoleId: this.ssoSettings.defaultRoleId || null,
          defaultRoleName: this.ssoSettings.defaultRoleName || null,
          facilityIds: [],
        };
        this.originalProvisioningSettings = structuredClone(this.provisioningSettings);
      }
    },
    async initiateAdminConsent() {
      this.connectLoading = true;
      try {
        const res = await ssoService.initiateAdminConsent();
        if (res.data?.consentUrl) {
          window.location.href = res.data.consentUrl;
        } else {
          throw new Error('No consent URL received');
        }
      } catch (err) {
        error('Failed to initiate admin consent:', err);
        this.$dialog.notify.error('Failed to initiate SSO connection');
        this.connectLoading = false;
      }
    },
    async testSSOConnection() {
      this.testConnection.loading = true;
      this.testConnection.success = false;
      this.testConnection.error = false;
      this.testConnection.message = '';

      try {
        // For connected SSO (using shared app), test without credentials
        // Backend will use stored configuration
        const res = await ssoService.testConnection({
          azureTenantId: this.ssoSettings.azureTenantId,
          azureClientId: this.ssoSettings.azureClientId,
        });

        if (res.data.success) {
          this.testConnection.success = true;
          this.testConnection.message = res.data.tenantName
            ? `Connected to ${res.data.tenantName}`
            : 'Connection verified';
        } else {
          this.testConnection.error = true;
          this.testConnection.message = res.data.error || 'Connection failed';
        }
      } catch (err) {
        error('SSO connection test failed:', err);
        this.testConnection.error = true;
        this.testConnection.message = err.response?.data?.error || 'Connection test failed';
      } finally {
        this.testConnection.loading = false;
        // Clear message after 3 seconds
        setTimeout(() => {
          this.testConnection.message = '';
        }, 3000);
      }
    },
    // ============================================
    // Authentication Settings Methods
    // ============================================

    async saveAuthSettings() {
      this.savingAuth = true;
      try {
        const res = await ssoService.updateAuthenticationSettings({
          enableSsoLogin: this.authSettings.enableSsoLogin,
          allowPasswordLogin: this.authSettings.allowPasswordLogin,
        });
        if (res.data) {
          this.authSettings = { ...res.data };
        }
        this.originalAuthSettings = structuredClone(this.authSettings);
        this.$dialog.notify.success('Authentication settings saved', { position: 'top-right', timeout: 2000 });
      } catch (err) {
        error('Failed to save authentication settings:', err);
        this.$dialog.notify.error(err.response?.data?.error || 'Failed to save authentication settings');
      } finally {
        this.savingAuth = false;
      }
    },

    resetAuthSettings() {
      this.authSettings = structuredClone(this.originalAuthSettings);
    },

    // ============================================
    // User Provisioning Settings Methods
    // ============================================

    async saveProvisioningSettings() {
      // Validate: defaultRoleId is required when autoCreateUsers is true
      if (this.provisioningSettings.autoCreateUsers && !this.provisioningSettings.defaultRoleId) {
        this.$dialog.notify.error('Please select a default role for auto-provisioned users');
        return;
      }

      this.savingProvisioning = true;
      try {
        const res = await ssoService.updateProvisioningSettings({
          autoCreateUsers: this.provisioningSettings.autoCreateUsers,
          defaultRoleId: this.provisioningSettings.defaultRoleId,
          facilityIds: this.provisioningSettings.facilityIds,
        });
        if (res.data) {
          this.provisioningSettings = {
            ...res.data,
            facilityIds: res.data.facilityIds || [],
          };
        }
        this.originalProvisioningSettings = structuredClone(this.provisioningSettings);
        this.$dialog.notify.success('User provisioning settings saved', { position: 'top-right', timeout: 2000 });
      } catch (err) {
        error('Failed to save provisioning settings:', err);
        this.$dialog.notify.error(err.response?.data?.error || 'Failed to save provisioning settings');
      } finally {
        this.savingProvisioning = false;
      }
    },

    resetProvisioningSettings() {
      this.provisioningSettings = structuredClone(this.originalProvisioningSettings);
    },

    selectAllFacilities() {
      this.provisioningSettings.facilityIds = this.facilities.map(f => f.id);
    },

    deselectAllFacilities() {
      this.provisioningSettings.facilityIds = [];
    },

    removeFacility(facilityId) {
      this.provisioningSettings.facilityIds = this.provisioningSettings.facilityIds.filter(id => id !== facilityId);
    },
    confirmDisconnect() {
      this.disconnectDialog = true;
    },
    async disconnectSSO() {
      this.disconnecting = true;
      try {
        await ssoService.disconnect();
        // Reset to disconnected state - reload to get fresh state from server
        await this.loadSettings();
        this.disconnectDialog = false;
        this.expandedPanels = [0];
        this.$dialog.notify.success('SSO disconnected', { position: 'top-right', timeout: 2000 });
      } catch (err) {
        error('Failed to disconnect SSO:', err);
        this.$dialog.notify.error(err.response?.data?.error || 'Failed to disconnect');
      } finally {
        this.disconnecting = false;
      }
    },
    handleConsentCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const consentSuccess = urlParams.get('consent');
      const errorParam = urlParams.get('error');
      const orgName = urlParams.get('org');

      if (consentSuccess === 'success') {
        const message = orgName
          ? `Connected to ${decodeURIComponent(orgName)}`
          : 'SSO connected successfully';
        this.$dialog.notify.success(message, { position: 'top-right', timeout: 3000 });
        window.history.replaceState({}, document.title, window.location.pathname);
        this.loadSettings();
      } else if (errorParam) {
        const errorDesc = urlParams.get('error_description') || errorParam;
        this.$dialog.notify.error(`Connection failed: ${decodeURIComponent(errorDesc)}`, { position: 'top-right', timeout: 5000 });
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    },

    // ============================================
    // Domain Management Methods
    // ============================================

    openAddDomainDialog() {
      this.addDomainForm = {
        valid: false,
        loading: false,
        domain: '',
        notes: '',
      };
      this.addDomainDialog = true;
      this.$nextTick(() => {
        this.$refs.addDomainForm?.resetValidation();
      });
    },

    closeAddDomainDialog() {
      this.addDomainDialog = false;
      this.addDomainForm = {
        valid: false,
        loading: false,
        domain: '',
        notes: '',
      };
    },

    async addDomain() {
      if (!this.$refs.addDomainForm?.validate()) return;

      this.addDomainForm.loading = true;
      try {
        const res = await ssoService.addVerifiedDomain({
          domain: this.addDomainForm.domain.toLowerCase().trim(),
          notes: this.addDomainForm.notes?.trim() || null,
        });

        // Add new domain to local state
        if (res.data) {
          if (!this.ssoSettings.verifiedDomains) {
            this.ssoSettings.verifiedDomains = [];
          }
          this.ssoSettings.verifiedDomains.push(res.data);
          this.originalSettings = structuredClone(this.ssoSettings);
        }

        this.$dialog.notify.success(`Domain "${this.addDomainForm.domain}" added`, { position: 'top-right', timeout: 2000 });
        this.closeAddDomainDialog();
      } catch (err) {
        error('Failed to add domain:', err);
        const errorMsg = err.response?.data?.error || 'Failed to add domain';
        this.$dialog.notify.error(errorMsg);
      } finally {
        this.addDomainForm.loading = false;
      }
    },

    confirmDeleteDomain(domain) {
      this.domainToDelete = domain;
      this.deleteDomainDialog = true;
    },

    async deleteDomain() {
      if (!this.domainToDelete) return;

      this.deletingDomainId = this.domainToDelete.id;
      try {
        await ssoService.removeVerifiedDomain(this.domainToDelete.id);

        // Remove from local state
        const index = this.ssoSettings.verifiedDomains?.findIndex(d => d.id === this.domainToDelete.id);
        if (index > -1) {
          this.ssoSettings.verifiedDomains.splice(index, 1);
          this.originalSettings = structuredClone(this.ssoSettings);
        }

        this.$dialog.notify.success(`Domain "${this.domainToDelete.domain}" removed`, { position: 'top-right', timeout: 2000 });
        this.deleteDomainDialog = false;
        this.domainToDelete = null;
      } catch (err) {
        error('Failed to remove domain:', err);
        const errorMsg = err.response?.data?.error || 'Failed to remove domain';
        this.$dialog.notify.error(errorMsg);
      } finally {
        this.deletingDomainId = null;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
  },
  mounted() {
    this.handleConsentCallback();
    this.loadSettings();
  },
};
</script>

<style scoped>
/* Panel Styles */
.sso-panel {
  background: var(--v-bg2-base) !important;
  border: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12)) !important;
  border-radius: 8px !important;
  margin-bottom: 12px !important;
}

.sso-panel::before {
  box-shadow: none !important;
}

.sso-panel-header {
  min-height: 48px !important;
  padding: 12px 16px !important;
}

.sso-panel-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--v-bg3-base);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sso-panel-icon--connected {
  background: var(--v-success-base);
}

.sso-panel-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--v-text-base);
}

/* Microsoft Card */
.sso-microsoft-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--v-bg3-base);
  border: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.12));
  border-radius: 8px;
}

.sso-microsoft-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.sso-microsoft-card__info {
  display: flex;
  flex-direction: column;
}

.sso-microsoft-card__name {
  font-size: 13px;
  font-weight: 500;
  color: var(--v-text-base);
}

.sso-microsoft-card__desc {
  font-size: 11px;
  color: var(--v-text_secondary-base);
}

.sso-admin-hint {
  font-size: 11px;
  color: var(--v-text_secondary-base);
  margin: 10px 0 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.sso-provider-logo {
  flex-shrink: 0;
}

.sso-provider-logo--small {
  width: 16px;
  height: 16px;
}

.sso-connect-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
}

/* Connected State */
.sso-connected-section {
  padding: 4px 0;
}

.sso-connected-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sso-connected-provider {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sso-connected-info {
  display: flex;
  flex-direction: column;
}

.sso-connected-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--v-text-base);
}

.sso-connected-tenant {
  font-size: 11px;
  color: var(--v-text_secondary-base);
  font-family: 'Roboto Mono', monospace;
}

.sso-connected-meta {
  font-size: 10px;
  color: var(--v-text_secondary-base);
  opacity: 0.7;
}

.sso-connected-actions {
  display: flex;
  gap: 4px;
}

/* Settings List */
.sso-settings-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sso-setting-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--v-bg3-base);
  border-radius: 6px;
}

.sso-setting-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sso-setting-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--v-text-base);
  display: flex;
  align-items: center;
  gap: 6px;
}

.sso-setting-warning {
  font-size: 11px;
  color: var(--v-warning-base);
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Role Selection */
.sso-role-row {
  padding: 12px;
  background: var(--v-bg3-base);
  border-radius: 6px;
  margin-top: 12px;
}

.sso-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--v-text_secondary-base);
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.sso-role-select {
  max-width: 300px;
}

.sso-role-current {
  font-size: 11px;
  color: var(--v-text_secondary-base);
  margin-top: 4px;
}

/* Section Actions (inline save/discard buttons) */
.sso-section-actions {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.08));
}

/* Verified Domains Section */
.sso-domains-section {
  padding: 4px 0;
}

.sso-domains-description {
  font-size: 12px;
  color: var(--v-text_secondary-base);
  margin: 0 0 12px;
  display: flex;
  align-items: center;
}

.sso-domains-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sso-domain-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: var(--v-bg3-base);
  border-radius: 6px;
  border: 1px solid var(--ds-border-default, rgba(0, 0, 0, 0.08));
}

.sso-domain-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.sso-domain-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--v-text-base);
  display: flex;
  align-items: center;
}

.sso-domain-meta {
  font-size: 11px;
  color: var(--v-text_secondary-base);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sso-domains-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--v-bg3-base);
  border-radius: 6px;
  gap: 8px;
  color: var(--v-text_secondary-base);
  font-size: 13px;
}

/* Facilities select - chip overflow */
.sso-facilities-select ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}

.sso-add-domain-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
}

/* Dark mode */
.theme--dark .sso-panel {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

/* Responsive */
@media (max-width: 600px) {
  .sso-microsoft-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .sso-microsoft-card .sso-connect-btn {
    width: 100%;
  }

  .sso-connected-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .sso-connected-actions {
    align-self: flex-end;
  }
}
</style>
